/**
 * Client Onboarding — auto-send a welcome email, tailored intake form, and
 * any additional questionnaires when a client books a mapped assessment type.
 *
 * Per-appointment-type trigger contains:
 *   - intake template (existing)
 *   - list of questionnaire templates (new)
 *   - welcome subject + HTML body (new; supports {first_name},{appointment_time},{location},{practitioner},{intake_link})
 *   - reminder_days_before scheduled follow-up (new; 0 disables)
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ClipboardList, Plus, Save, Trash2, ChevronDown, ChevronRight, Send, Search, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const DEFAULT_WELCOME_HTML = `<p>Hi {first_name},</p>
<p>Welcome to Living Energy Brain Body Rehab. Your appointment is on <strong>{appointment_time}</strong>.</p>
<p>Before you arrive, please complete the forms below — it takes just a few minutes and saves us paperwork on the day.</p>`;

// Small helper component: pushes every non-submitted onboarding link
// out to a fresh expiry from now, including already-expired ones.
// Used from the Settings header so operators can extend everything
// after they've bumped `link_expiry_days`.
function ExtendAllLinksButton({ expiryDays }) {
  const [busy, setBusy] = useState(false);
  async function run() {
    if (!window.confirm(
      `Extend every currently-sent onboarding link (including expired ones) to a fresh ${expiryDays}-day window from now?`,
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/intake/links/extend-all", {
        include_expired: true,
      });
      const { extended = 0, expired_revived: revived = 0 } = r.data || {};
      const total = extended + revived;
      if (total === 0) {
        toast.info("Nothing to extend", {
          description: "No pending or expired links found.",
        });
      } else {
        toast.success(`${total} link${total === 1 ? "" : "s"} extended`, {
          description: revived
            ? `${extended} refreshed · ${revived} expired link${revived === 1 ? "" : "s"} revived`
            : `${extended} refreshed`,
        });
      }
    } catch (e) {
      toast.error("Extend failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={run}
      disabled={busy}
      className="h-9"
      data-testid="intake-extend-all-btn"
    >
      {busy ? (
        <>
          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          Extending…
        </>
      ) : (
        <>
          <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
          Extend all pending &amp; expired links
        </>
      )}
    </Button>
  );
}

export default function ClientOnboardingSettings() {
  const [cfg, setCfg] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [apptTypes, setApptTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [expanded, setExpanded] = useState({});   // idx -> boolean

  async function load() {
    try {
      const [c, t, a] = await Promise.all([
        api.get("/intake/config"),
        api.get("/intake/templates"),
        api.get("/appointment-types").catch(() => ({ data: [] })),
      ]);
      setCfg(c.data);
      setTemplates(t.data || []);
      setApptTypes(a.data || []);
      setDraft(c.data);
    } catch (e) {
      toast.error("Could not load onboarding settings", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }
  useEffect(() => { load(); }, []);

  // Sort intake / questionnaire templates so v2.0 (newest templateVersion)
  // appears above legacy v1 rows with the same name. This makes the
  // dropdown consistently offer the v2 form as the top pick when v1 and
  // v2 twins share an identical `name`.
  const _versionSort = (a, b) => {
    const nameCmp = (a.name || "").localeCompare(b.name || "");
    if (nameCmp !== 0) return nameCmp;
    // Higher templateVersion first (v2.0 > v1.0 > undefined)
    const va = parseFloat(a.templateVersion || "0");
    const vb = parseFloat(b.templateVersion || "0");
    return vb - va;
  };
  const intakeTemplates = useMemo(
    () => templates.filter((t) => !t.kind || t.kind === "intake").sort(_versionSort),
    [templates],
  );
  const questionnaireTemplates = useMemo(
    () => templates.filter((t) => t.kind === "questionnaire").sort(_versionSort),
    [templates],
  );

  const triggerOptions = useMemo(() => {
    if (!apptTypes?.length) return [];
    return apptTypes
      .filter((t) => t.id)
      .map((t) => ({ id: t.id, name: t.name }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [apptTypes]);

  function update(patch) { setDraft((d) => ({ ...d, ...patch })); }

  function addTrigger() {
    update({
      triggers: [
        ...(draft.triggers || []),
        {
          appt_type_id: 0,
          template_id: intakeTemplates[0]?.id || "",
          name: "",
          questionnaire_template_ids: [],
          welcome_subject: "",
          welcome_html: DEFAULT_WELCOME_HTML,
          reminder_days_before: 0,
          first_time_only_for_type: false,
        },
      ],
    });
    setExpanded((e) => ({ ...e, [(draft.triggers || []).length]: true }));
  }
  function updateTrigger(idx, patch) {
    update({
      triggers: (draft.triggers || []).map((t, i) => i === idx ? { ...t, ...patch } : t),
    });
  }
  function removeTrigger(idx) {
    update({ triggers: (draft.triggers || []).filter((_, i) => i !== idx) });
  }
  function toggleQuestionnaire(idx, tplId) {
    const t = (draft.triggers || [])[idx];
    const cur = t.questionnaire_template_ids || [];
    const next = cur.includes(tplId)
      ? cur.filter((x) => x !== tplId)
      : [...cur, tplId];
    updateTrigger(idx, { questionnaire_template_ids: next });
  }

  async function save() {
    setSaving(true);
    try {
      const triggers = (draft.triggers || [])
        .filter((t) => t.appt_type_id && t.template_id)
        .map((t) => ({
          appt_type_id: Number(t.appt_type_id),
          template_id: t.template_id,
          name: t.name || "",
          questionnaire_template_ids: t.questionnaire_template_ids || [],
          welcome_subject: t.welcome_subject || "",
          welcome_html: t.welcome_html || "",
          reminder_days_before: Number(t.reminder_days_before) || 0,
          first_time_only_for_type: !!t.first_time_only_for_type,
        }));
      const r = await api.post("/intake/config", {
        enabled: !!draft.enabled,
        brand_name: draft.brand_name || "",
        triggers,
        reminder_hours_before_appt: Number(draft.reminder_hours_before_appt) || 24,
        link_expiry_days: Number(draft.link_expiry_days) || 60,
      });
      setCfg(r.data); setDraft(r.data);
      toast.success("Onboarding settings saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  async function sendTest(t) {
    if (!t.appt_type_id || !t.template_id) {
      toast.error("Save the mapping first", {
        description: "Pick an appointment type + intake form before sending a test.",
      });
      return;
    }
    const to = window.prompt(
      "Send test onboarding email to which address?\n\n" +
      "This renders your welcome copy + every questionnaire link with a [TEST] banner. " +
      "No submission rows are created — the links are placeholders.",
      cfg?.brand_name ? "" : "",
    );
    if (to === null) return;
    const trimmed = (to || "").trim();
    if (!trimmed) {
      toast.error("Enter an email address");
      return;
    }
    try {
      const r = await api.post("/intake/test-onboarding-email", {
        to_email: trimmed,
        appt_type_id: Number(t.appt_type_id),
        first_name: "Test",
      });
      if (r.data.ok) {
        toast.success(`Test sent to ${r.data.to}`, {
          description: `Includes ${r.data.items.length} item${r.data.items.length === 1 ? "" : "s"}: ${r.data.items.map((i) => i.label || i.name || i.url).join(" · ")}`,
        });
      } else {
        toast.error("Send failed", {
          description: "Resend may not be configured, or the recipient is invalid.",
        });
      }
    } catch (e) {
      toast.error("Send failed", { description: e?.response?.data?.detail || e.message });
    }
  }


  if (!cfg || !draft) {
    return (
      <Card className="p-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="text-sm text-zinc-500">Loading…</div>
      </Card>
    );
  }

  const hasIntakeTemplates = intakeTemplates.length > 0;

  return (
    <Card
      className="p-6 rounded-lg border border-zinc-200 shadow-none"
      data-testid="intake-settings-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <ClipboardList className="h-3.5 w-3.5" /> Client Onboarding
          </div>
          <h2 className="mt-1 font-display text-2xl font-bold text-zinc-950">
            Welcome, intake &amp; questionnaires
          </h2>
          <p className="mt-1 text-sm text-zinc-600 max-w-2xl leading-relaxed">
            When a client books a mapped assessment type they get one combined email with your welcome copy plus a link to every form they need to complete: the intake form and any additional questionnaires. Reminders can be scheduled per assessment type.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label htmlFor="intake-enabled" className="text-sm text-zinc-700">Enabled</Label>
          <Switch
            id="intake-enabled"
            checked={!!draft.enabled}
            onCheckedChange={(v) => update({ enabled: v })}
            data-testid="intake-enabled-switch"
          />
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <Label className="text-sm">Brand name (shown in emails &amp; form header)</Label>
          <Input
            value={draft.brand_name || ""}
            onChange={(e) => update({ brand_name: e.target.value })}
            className="mt-1.5 bg-white"
            data-testid="intake-brand-input"
          />
        </div>
        <div>
          <Label className="text-sm">Default reminder (hours before appt)</Label>
          <Input
            type="number" min="0" max="168"
            value={draft.reminder_hours_before_appt ?? 24}
            onChange={(e) => update({ reminder_hours_before_appt: e.target.value })}
            className="mt-1.5 bg-white"
          />
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-3 gap-4 items-end">
        <div>
          <Label className="text-sm">Link expiry (days)</Label>
          <Input
            type="number" min="1" max="365"
            value={draft.link_expiry_days ?? 60}
            onChange={(e) => update({ link_expiry_days: e.target.value })}
            className="mt-1.5 bg-white"
            data-testid="intake-link-expiry-input"
          />
          <p className="text-2xs text-zinc-500 mt-1">
            How long an onboarding link stays clickable after being sent.
          </p>
        </div>
        <div className="sm:col-span-2 flex flex-col items-start gap-2">
          <Label className="text-sm">Refresh existing links</Label>
          <ExtendAllLinksButton expiryDays={Number(draft.link_expiry_days) || 60} />
          <p className="text-2xs text-zinc-500">
            Pushes every currently-sent link — including already-expired ones — out to a fresh expiry from now.
          </p>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Per-appointment-type triggers</Label>
          <Button
            size="sm" variant="outline"
            onClick={addTrigger}
            disabled={!hasIntakeTemplates}
            data-testid="intake-add-trigger"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add mapping
          </Button>
        </div>
        {!hasIntakeTemplates && (
          <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded">
            Create at least one <b>intake</b> template on the Intake Forms page before adding a trigger mapping.
          </p>
        )}

        <div className="mt-3 space-y-3">
          {(draft.triggers || []).length === 0 && (
            <div className="text-sm text-zinc-500 py-2">
              No mappings yet. Add one to auto-send onboarding when a client books that appointment type.
            </div>
          )}

          {(draft.triggers || []).map((t, idx) => {
            const isOpen = !!expanded[idx];
            const qCount = (t.questionnaire_template_ids || []).length;
            return (
              <div
                key={idx}
                className="rounded-md border border-zinc-200 bg-zinc-50"
                data-testid={`intake-trigger-row-${idx}`}
              >
                {/* --- Compact top row --- */}
                <div className="grid sm:grid-cols-12 gap-2 items-end p-3">
                  <div className="sm:col-span-5">
                    <Label className="text-xs">Appointment type</Label>
                    <Select
                      value={String(t.appt_type_id || "")}
                      onValueChange={(v) => {
                        const t2 = triggerOptions.find((o) => String(o.id) === v);
                        updateTrigger(idx, { appt_type_id: Number(v), name: t2?.name || "" });
                      }}
                    >
                      <SelectTrigger className="bg-white" data-testid={`intake-trigger-appt-${idx}`}>
                        <SelectValue placeholder="Choose…" />
                      </SelectTrigger>
                      <SelectContent>
                        {triggerOptions.map((o) => (
                          <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-5">
                    <Label className="text-xs">Intake form</Label>
                    <Select
                      value={t.template_id || ""}
                      onValueChange={(v) => updateTrigger(idx, { template_id: v })}
                    >
                      <SelectTrigger className="bg-white" data-testid={`intake-trigger-template-${idx}`}>
                        <SelectValue placeholder="Choose…" />
                      </SelectTrigger>
                      <SelectContent>
                        {intakeTemplates.map((tpl) => (
                          <SelectItem key={tpl.id} value={tpl.id}>
                            <span className="flex items-center gap-1.5">
                              <span>{tpl.name}</span>
                              {tpl.templateVersion && (
                                <span className="text-2xs px-1 rounded border border-indigo-200 bg-indigo-50 text-indigo-800 font-mono-num">
                                  v{tpl.templateVersion}
                                </span>
                              )}
                              {!tpl.active && (
                                <span className="text-2xs px-1 rounded border border-red-200 bg-red-50 text-red-700">
                                  inactive
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-1">
                    <Button
                      size="sm" variant="outline" className="w-full"
                      onClick={() => setExpanded((e) => ({ ...e, [idx]: !isOpen }))}
                      data-testid={`intake-trigger-expand-${idx}`}
                      title={isOpen ? "Hide advanced" : "Show advanced"}
                    >
                      {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                  <div className="sm:col-span-1">
                    <Button
                      size="sm" variant="outline" className="w-full text-red-700"
                      onClick={() => removeTrigger(idx)}
                      data-testid={`intake-trigger-remove-${idx}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* --- Summary line (always visible) --- */}
                <div className="px-3 pb-2 text-2xs text-zinc-500 font-mono-num flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>{qCount} questionnaire{qCount === 1 ? "" : "s"}</span>
                  <span>•</span>
                  <span>welcome {t.welcome_subject || t.welcome_html ? "customised" : "default"}</span>
                  <span>•</span>
                  <span>reminder {t.reminder_days_before ? `${t.reminder_days_before}d before` : "off"}</span>
                  {t.first_time_only_for_type && (
                    <>
                      <span>•</span>
                      <span className="text-emerald-700">first-timers only</span>
                    </>
                  )}
                </div>

                {/* --- Advanced (welcome + questionnaires) --- */}
                {isOpen && (
                  <div className="border-t border-zinc-200 bg-white p-3 space-y-4">
                    <div>
                      <Label className="text-xs">Welcome email — subject</Label>
                      <Input
                        value={t.welcome_subject || ""}
                        onChange={(e) => updateTrigger(idx, { welcome_subject: e.target.value })}
                        placeholder={`Welcome to ${draft.brand_name || "our clinic"} — please complete your registration`}
                        className="mt-1.5 bg-white"
                        data-testid={`intake-trigger-welcome-subject-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">
                        Welcome email — body (HTML supported). Merge tags:{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{first_name}"}</code>{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{appointment_time}"}</code>{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{appointment_type}"}</code>{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{cost}"}</code>{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{location}"}</code>{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{practitioner}"}</code>{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{intake_link}"}</code>{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{intake_name}"}</code>{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{intake}"}</code>{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{questionnaire_1_link}"}</code>{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{questionnaire_1_name}"}</code>{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{questionnaire_1}"}</code>{" "}
                        <code className="text-2xs bg-zinc-100 px-1 rounded">{"{questionnaires_list}"}</code>
                      </Label>
                      <p className="mt-1 text-2xs text-zinc-500 leading-relaxed">
                        <code>{"{location}"}</code> pulls Acuity&apos;s appointment location; <code>{"{practitioner}"}</code> pulls the calendar / staff name;{" "}
                        <code>{"{appointment_type}"}</code> pulls the Acuity appointment type name; <code>{"{cost}"}</code> renders the appointment price formatted as AU dollars (blank when the appointment is free).
                        Numbered tags (<code>_1</code>, <code>_2</code>, …) map to questionnaires in the order they appear above. Use{" "}
                        <code>_link</code> for a bare URL, <code>_name</code> for the display name, or the bare tag (e.g.{" "}
                        <code>{"{questionnaire_1}"}</code>) for a ready-made HTML link. If you place any of these tags in the body, the auto-appended bulleted list is suppressed so YOUR layout wins.
                      </p>
                      <Textarea
                        value={t.welcome_html || ""}
                        onChange={(e) => updateTrigger(idx, { welcome_html: e.target.value })}
                        placeholder={DEFAULT_WELCOME_HTML}
                        rows={6}
                        className="mt-1.5 bg-white font-mono-num text-xs"
                        data-testid={`intake-trigger-welcome-html-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">
                        Additional questionnaires{" "}
                        <span className="text-zinc-500">({questionnaireTemplates.length} available)</span>
                      </Label>
                      {questionnaireTemplates.length === 0 ? (
                        <p className="mt-1.5 text-xs text-zinc-500 italic">
                          No questionnaire templates yet — create one on the Intake Forms page and set its <code>kind</code> to &ldquo;questionnaire&rdquo;.
                        </p>
                      ) : (
                        <div className="mt-1.5 grid sm:grid-cols-2 gap-1.5">
                          {questionnaireTemplates.map((qt) => {
                            const active = (t.questionnaire_template_ids || []).includes(qt.id);
                            return (
                              <button
                                type="button"
                                key={qt.id}
                                onClick={() => toggleQuestionnaire(idx, qt.id)}
                                className={cn(
                                  "text-left text-xs px-3 py-2 rounded border transition-colors flex items-center gap-1.5",
                                  active
                                    ? "bg-zinc-950 border-zinc-950 text-white"
                                    : "bg-white border-zinc-300 hover:border-zinc-500",
                                )}
                                data-testid={`intake-trigger-questionnaire-${idx}-${qt.id}`}
                              >
                                <span>{active ? "✓ " : ""}{qt.name}</span>
                                {qt.templateVersion && (
                                  <span className={cn(
                                    "text-2xs px-1 rounded border font-mono-num",
                                    active
                                      ? "border-white/40 bg-white/10 text-white"
                                      : "border-indigo-200 bg-indigo-50 text-indigo-800",
                                  )}>
                                    v{qt.templateVersion}
                                  </span>
                                )}
                                {!qt.active && (
                                  <span className="text-2xs px-1 rounded border border-red-200 bg-red-50 text-red-700">
                                    inactive
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="grid sm:grid-cols-3 gap-2 items-end">
                      <div>
                        <Label className="text-xs">Reminder (days before appt)</Label>
                        <Input
                          type="number" min="0" max="60"
                          value={t.reminder_days_before ?? 0}
                          onChange={(e) => updateTrigger(idx, { reminder_days_before: e.target.value })}
                          className="mt-1.5 bg-white"
                          data-testid={`intake-trigger-reminder-days-${idx}`}
                        />
                        <p className="mt-1 text-2xs text-zinc-500">0 = disabled</p>
                      </div>
                      <div className="sm:col-span-2 flex flex-col justify-end gap-2">
                        <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!t.first_time_only_for_type}
                            onChange={(e) => updateTrigger(idx, { first_time_only_for_type: e.target.checked })}
                            className="mt-0.5"
                            data-testid={`intake-trigger-first-time-only-${idx}`}
                          />
                          <span>
                            <span className="font-medium">Only send to first-timers</span>
                            <span className="block text-2xs text-zinc-500 leading-snug">
                              Skip this welcome when the client already has another
                              non-cancelled booking of this appointment type. Perfect
                              for class formats (Reformer, group Pilates) where you
                              only want to onboard new attendees.
                            </span>
                          </span>
                        </label>
                        <div className="flex justify-end">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => sendTest(t)}
                            className="border-zinc-300 hover:bg-zinc-100"
                            data-testid={`intake-trigger-send-test-${idx}`}
                            title="Send a [TEST] preview of this trigger's welcome email + form links to any address"
                          >
                            <Send className="h-3.5 w-3.5 mr-1.5" /> Send test email
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-2">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="intake-settings-save"
        >
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </Card>
  );
}


/**
 * OnboardingDiagnostics — troubleshoot / force-send onboarding for a
 * specific Acuity appointment ID. Useful when a booking didn't trigger
 * an onboarding email as expected. Two actions:
 *
 *   • Diagnose  → walks every gate (config enabled → trigger match →
 *     template exists → existing submission → first-timer gate) and
 *     tells the operator exactly which step failed.
 *   • Send now  → runs the FULL onboarding dispatch (intake form + all
 *     configured questionnaires + welcome email). Mirrors what the
 *     webhook would have done.
 */
export function OnboardingDiagnostics() {
  const [mode, setMode] = useState("appointment"); // "appointment" | "client"
  const [apptId, setApptId] = useState("");
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [diagnosing, setDiagnosing] = useState(null); // appt_id while in-flight
  const [sending, setSending] = useState(null);       // appt_id while in-flight
  const [result, setResult] = useState(null);   // { kind, data, apptId? }
  const [webhookLog, setWebhookLog] = useState(null);
  const [loadingLog, setLoadingLog] = useState(false);

  async function loadLog() {
    setLoadingLog(true);
    try {
      const r = await api.get("/acuity/webhook-log", { params: { limit: 30 } });
      setWebhookLog(r.data.rows || []);
    } catch (e) {
      toast.error("Could not load webhook log", { description: e?.response?.data?.detail || e.message });
    } finally { setLoadingLog(false); }
  }
  useEffect(() => { loadLog(); }, []);

  async function searchClient() {
    const q = clientQuery.trim();
    if (q.length < 2) return toast.error("Type at least 2 characters");
    setSearching(true);
    setClientResults(null);
    setResult(null);
    try {
      const r = await api.get("/intake/lookup-client", { params: { q } });
      setClientResults(r.data);
      if (!(r.data.clients || []).length) {
        toast.info("No matching clients in Acuity for that query");
      }
    } catch (e) {
      toast.error("Lookup failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSearching(false); }
  }

  async function diagnose(id) {
    const targetId = id ?? apptId;
    if (!targetId) return toast.error("Enter an Acuity appointment ID");
    setDiagnosing(String(targetId));
    setResult(null);
    try {
      const r = await api.get(`/intake/diagnose/${encodeURIComponent(targetId)}`);
      setResult({ kind: "diagnose", data: r.data, apptId: targetId });
    } catch (e) {
      toast.error("Diagnose failed", { description: e?.response?.data?.detail || e.message });
    } finally { setDiagnosing(null); }
  }

  async function forceSend(id) {
    const targetId = id ?? apptId;
    if (!targetId) return toast.error("Enter an Acuity appointment ID");
    if (!window.confirm(`Send full onboarding (welcome email + intake form + all questionnaires) for appointment ${targetId}?`)) return;
    setSending(String(targetId));
    setResult(null);
    try {
      const r = await api.post("/intake/send", { appointment_id: Number(targetId) });
      setResult({ kind: "send", data: r.data, apptId: targetId });
      toast.success(
        r.data.mode === "onboarding"
          ? `Onboarding sent · intake + ${r.data.questionnaires_sent} questionnaire(s)`
          : "Intake form sent",
      );
    } catch (e) {
      toast.error("Send failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSending(null); }
  }

  return (
    <Card className="p-6 rounded-lg border border-zinc-200 shadow-none" data-testid="onboarding-diagnostics-card">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
        <Search className="h-3.5 w-3.5" /> Onboarding troubleshooter
      </div>
      <div className="mt-1 text-sm text-zinc-700">
        Look up a client by name or email — or paste an Acuity appointment ID — then
        diagnose why the onboarding email didn't fire, or force a send now.
      </div>

      <div className="mt-4 inline-flex rounded-md border border-zinc-200 bg-white p-0.5" data-testid="diagnose-mode-toggle">
        {[
          { k: "client",      label: "Client (name / email)" },
          { k: "appointment", label: "Appointment ID" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => { setMode(t.k); setResult(null); }}
            className={`text-2xs px-3 py-1 rounded ${mode === t.k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
            data-testid={`diagnose-mode-${t.k}`}
          >{t.label}</button>
        ))}
      </div>

      {mode === "appointment" && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Acuity appointment ID</Label>
            <Input
              value={apptId}
              onChange={(e) => setApptId(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="e.g. 1743672253"
              className="mt-1 font-mono-num bg-white"
              data-testid="diagnose-appointment-id"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => diagnose()}
            disabled={!!diagnosing || !apptId}
            data-testid="diagnose-appointment-btn"
          >
            {diagnosing === String(apptId) ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}
            Diagnose
          </Button>
          <Button
            onClick={() => forceSend()}
            disabled={!!sending || !apptId}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="force-send-onboarding-btn"
          >
            {sending === String(apptId) ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
            Send now
          </Button>
        </div>
      )}

      {mode === "client" && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Client name or email</Label>
              <Input
                value={clientQuery}
                onChange={(e) => setClientQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") searchClient(); }}
                placeholder="e.g. Penny Weber  or  penny@example.com"
                className="mt-1 bg-white"
                data-testid="diagnose-client-query"
              />
            </div>
            <Button
              variant="outline"
              onClick={searchClient}
              disabled={searching || clientQuery.trim().length < 2}
              data-testid="diagnose-client-search-btn"
            >
              {searching ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}
              Find client
            </Button>
          </div>

          {clientResults && (clientResults.clients || []).length === 0 && (
            <div className="text-xs text-zinc-500 italic bg-zinc-50 border border-zinc-200 rounded px-3 py-2">
              No matching clients in Acuity for "{clientResults.query}".
            </div>
          )}

          {(clientResults?.clients || []).map((c) => (
            <div key={c.email}
              className="rounded-md border border-zinc-200 bg-white"
              data-testid={`diagnose-client-card-${c.email}`}
            >
              <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-zinc-900">
                    {(c.first_name || "") + " " + (c.last_name || "")}
                  </div>
                  <div className="text-2xs text-zinc-500 font-mono-num">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                </div>
                <div className="text-2xs text-zinc-500">{c.appointments.length} appointment{c.appointments.length === 1 ? "" : "s"}</div>
              </div>
              {c.appointments.length === 0 && (
                <div className="px-3 py-2 text-xs text-zinc-500 italic">No appointments in the lookup window.</div>
              )}
              <div className="divide-y divide-zinc-100">
                {c.appointments.map((a) => {
                  const idStr = String(a.id);
                  const date = a.datetime ? new Date(a.datetime).toLocaleString() : "—";
                  return (
                    <div key={a.id} className="px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-zinc-900">
                          {a.appointment_type || "Appointment"} <span className="text-2xs text-zinc-500">· ID {idStr}</span>
                          {a.canceled && (
                            <span className="ml-2 inline-block text-2xs uppercase bg-red-50 text-red-700 border border-red-200 rounded px-1">cancelled</span>
                          )}
                        </div>
                        <div className="text-2xs text-zinc-500">
                          {date}
                          {a.calendar && <span className="ml-2">· {a.calendar}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline" size="sm" className="text-2xs h-7"
                          onClick={() => diagnose(a.id)}
                          disabled={diagnosing === idStr}
                          data-testid={`diagnose-client-appt-diagnose-${a.id}`}
                        >
                          {diagnosing === idStr ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Search className="h-3 w-3 mr-1" />}
                          Diagnose
                        </Button>
                        <Button
                          size="sm" className="text-2xs h-7 bg-zinc-950 hover:bg-zinc-800 text-white"
                          onClick={() => forceSend(a.id)}
                          disabled={sending === idStr}
                          data-testid={`diagnose-client-appt-send-${a.id}`}
                        >
                          {sending === idStr ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                          Send now
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {result?.kind === "diagnose" && (
        <DiagnoseResult data={result.data} />
      )}
      {result?.kind === "send" && (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="diagnose-send-success">
          <div className="flex items-center gap-2 font-semibold">
            <CheckCircle2 className="h-4 w-4" /> Sent{result.apptId ? ` · appointment ${result.apptId}` : ""}
          </div>
          <div className="text-xs mt-1">
            {result.data.mode === "onboarding"
              ? `Full onboarding: intake form + ${result.data.questionnaires_sent} questionnaire(s).`
              : "Legacy single-form path (no trigger matched)."}
          </div>
          {result.data.link && (
            <div className="text-xs mt-1 break-all">
              Intake link: <a href={result.data.link} className="underline" target="_blank" rel="noreferrer">{result.data.link}</a>
            </div>
          )}
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-zinc-200">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
              Recent Acuity webhook hits
            </div>
            <div className="text-2xs text-zinc-500 mt-0.5">
              Newest 30. If your booking isn&apos;t listed, Acuity never called us — check the webhook is registered.
            </div>
          </div>
          <Button
            size="sm" variant="outline"
            onClick={loadLog} disabled={loadingLog}
            data-testid="webhook-log-refresh"
          >
            {loadingLog ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Refresh"}
          </Button>
        </div>
        <div className="mt-3 overflow-x-auto">
          {loadingLog && !webhookLog ? (
            <div className="text-xs text-zinc-500 py-2">Loading…</div>
          ) : !webhookLog || webhookLog.length === 0 ? (
            <div className="text-sm text-zinc-500 py-4 text-center border border-dashed border-zinc-200 rounded-md">
              No webhook hits recorded yet. Acuity hasn&apos;t called <code>/api/acuity/webhook</code>.
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="webhook-log-table">
              <thead>
                <tr className="text-left uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Action</th>
                  <th className="py-2 pr-3">Acuity ID</th>
                  <th className="py-2 pr-3">Sig</th>
                  <th className="py-2 pr-3">Result</th>
                </tr>
              </thead>
              <tbody>
                {webhookLog.map((r, i) => (
                  <tr key={i} className="border-b border-zinc-100" data-testid={`webhook-log-row-${i}`}>
                    <td className="py-2 pr-3 font-mono-num text-zinc-700">
                      {new Date(r.at).toLocaleString()}
                    </td>
                    <td className="py-2 pr-3">{r.action || "—"}</td>
                    <td className="py-2 pr-3 font-mono-num">{r.acuity_id || "—"}</td>
                    <td className="py-2 pr-3">
                      {r.signature_ok ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" /> ok
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-700">
                          <AlertCircle className="h-3 w-3" /> fail
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-zinc-700">
                      {r.error ? (
                        <span className="text-red-700" title={r.error}>× {r.error.slice(0, 40)}</span>
                      ) : r.dispatched ? (
                        <span className="text-emerald-700">✓ {r.dispatched}</span>
                      ) : r.ignored_reason ? (
                        <span className="text-zinc-500">— {r.ignored_reason}</span>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Card>
  );
}

function DiagnoseResult({ data }) {
  const ok = data.would_send;
  return (
    <div
      className={cn(
        "mt-4 rounded-md border p-3 text-sm",
        ok ? "border-emerald-200 bg-emerald-50 text-emerald-900"
           : "border-amber-200 bg-amber-50 text-amber-900",
      )}
      data-testid="diagnose-result"
    >
      <div className="flex items-center gap-2 font-semibold">
        {ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
        {ok ? "Would send" : `Would NOT send · ${data.reason}`}
      </div>
      {data.detail && <div className="text-xs mt-1">{data.detail}</div>}
      {data.trace?.length > 0 && (
        <details className="mt-2">
          <summary className="text-xs cursor-pointer text-zinc-600 hover:text-zinc-900">
            Step-by-step trace ({data.trace.length} steps)
          </summary>
          <pre className="mt-1 whitespace-pre-wrap text-2xs font-mono-num bg-white/60 border border-white/60 rounded p-2 max-h-80 overflow-auto">
            {JSON.stringify(data.trace, null, 2)}
          </pre>
        </details>
      )}
      {data.submission && (
        <div className="text-2xs mt-2">
          Existing submission token: <code className="font-mono-num">{data.submission.token}</code>
          {data.submission.sent_at && <> · sent {new Date(data.submission.sent_at).toLocaleString()}</>}
        </div>
      )}
    </div>
  );
}
